const roundCurrency = (value: number): number =>
  Number((Number.isFinite(value) ? value : 0).toFixed(2));

/**
 * Resolve how much the customer actually paid for a product order.
 *
 * `amountPaid` from the API can incorrectly store a processing-fee fragment
 * (e.g. ₦1.79) instead of the charged total. Product orders are paid in full
 * online, so when the reported amount is below goods+tax we fall back to the
 * known payable / goods total.
 */
export function resolveProductOrderAmountPaid({
  amountPaid,
  goodsTotal,
  chargedTotal,
}: {
  amountPaid?: number | null;
  /** Subtotal + tax (what the order total is when salon bears the fee). */
  goodsTotal: number;
  /** Amount sent to the payment provider, when known. */
  chargedTotal?: number | null;
}): number {
  const goods = roundCurrency(goodsTotal);
  const reported = roundCurrency(Number(amountPaid ?? 0));
  const charged = roundCurrency(Number(chargedTotal ?? 0));

  if (charged > 0 && charged + 0.009 >= goods) {
    return charged;
  }
  if (reported > 0 && reported + 0.009 >= goods) {
    return reported;
  }
  return goods;
}

/**
 * Show processing fee on the customer receipt only when it was included in
 * what they paid (salon did not bear the fee).
 */
export function customerPaidProcessingFee({
  processingFee,
  amountPaid,
  goodsTotal,
}: {
  processingFee?: number | null;
  amountPaid: number;
  goodsTotal: number;
}): number {
  const fee = roundCurrency(Number(processingFee ?? 0));
  if (fee <= 0) return 0;
  const goods = roundCurrency(goodsTotal);
  const paid = roundCurrency(amountPaid);
  return paid + 0.009 >= goods + fee ? fee : 0;
}
